/**
 * @file api-config provides promise-based HTTP abstraction with extended error
 */

import routerMain from '@/router'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useGeneralStore } from '@/stores/general'
import { useCompanyStore } from '@/stores/company'
import { ElNotification } from 'element-plus'
import companyService from '@/services/company.service'
import i18n from '@/i18n'

/* Server messages are passed through the catalogue the same way they were
   pre-migration: a known key is translated, anything else shown as-is. Checking
   `te` first matters because calling `t` on an unknown string logs an
   "[intlify] Not found" warning - which fired on every single API error. A
   non-string can't be rendered usefully, so it degrades to the generic label. */
const translate = value => {
  if (typeof value !== 'string') return i18n.global.t('Error')

  return i18n.global.te(value) ? i18n.global.t(value) : value
}

const $axios = axios.create({
  // setup root url for all http requests
  baseURL: process.env.VUE_APP_API,
  // customized XMLHttpRequest wrapper library
  headers: { 'X-Requested-With': 'XMLHttpRequest' }
})

// global request interceptor
$axios.interceptors.request.use(config => {
  const generalStore = useGeneralStore()
  const authStore = useAuthStore()
  generalStore.setLoading(true)
  const token = authStore.token
  // intercept url for auth
  if (token) {
    config.headers.Authorization = `Bearer ${token}` // eslint-disable-line no-param-reassign
    config.headers.Accept = 'application/json'
  }

  const companyStore = useCompanyStore()
  if (companyStore.fromWhiteLabeledDomain) {
    const attribute = config.method === 'get' ? 'params' : 'data'

    if (config[attribute]) {
      config[attribute].investor_portal_domain = companyService.getDomain()
    } else {
      config[attribute] = {
        investor_portal_domain: companyService.getDomain()
      }
    }
  }

  return config
})

// global response interceptor
$axios.interceptors.response.use(
  response => {
    const generalStore = useGeneralStore()
    generalStore.setLoading(false)
    return response
  },
  error => {
    const generalStore = useGeneralStore()
    const companyStore = useCompanyStore()
    generalStore.setLoading(false)

    /* `error.response` is absent for network failures, timeouts and cancelled
       requests - reading `.status` off it unguarded threw a TypeError from
       inside the interceptor, so no notification was shown at all. */
    const response = error.response
    let message = response && response.data && response.data.message ? response.data.message : error.message || error

    if (response) {
      // exception for file not found error
      if (response.status === 404 && response.data instanceof Blob) {
        message = 'Document not found'
      }
      // exception for Unauthorized
      if (response.status === 401) {
        message = 'Unauthorized'
        if (routerMain.currentRoute.value.name !== 'login') {
          routerMain.push({ ...companyStore.baseRouteConfig, name: 'login' })
        }
      }

      // Validation errors
      if (response.status === 422) {
        const errors = response.data.errors
        for (const field in errors) {
          message = Array.isArray(errors[field]) ? errors[field].join('<br/><br/>') : errors[field]
        }
      }
    }

    ElNotification.error({
      title: i18n.global.t('Error'),
      message: translate(message),
      position: 'bottom-right',
      dangerouslyUseHTMLString: true,
      duration: 8000
    })

    return Promise.reject(error.response)
  }
)

// In Vue 3, we don't use Vue.prototype
// Instead, we provide the axios instance through the app instance
// This will be configured in main.js

export default $axios
